import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Heart } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Nd1JobDetailsShell from "../nd1/components/Nd1JobDetailsShell";
import PageSeo from "../components/PageSeo";
import PageTopbar from "../components/PageTopbar";
import { DEFAULT_OG_IMAGE_PATH } from "../lib/pageSeo";
import {
  buildJobPageBreadcrumbItems,
  buildJobPageJsonLd,
  buildJobPageKeywords,
  buildJobPageSeoDescription,
  buildJobPageSeoTitle,
  isJobPubliclyIndexable,
} from "../lib/jobPostingSeo";
import JobDetailHeader from "../components/JobDetailHeader";
import JobListingContent from "../components/JobListingContent";
import JobPostCommentsSection from "../components/JobPostCommentsSection";
import ApplicantRejectionLetter from "../components/ApplicantRejectionLetter";
import JobuwinPageLoading from "../components/JobuwinPageLoading";
import PitchRecordModal from "../components/PitchRecordModal";
import { captureAnalyticsEvent } from "../lib/analytics";
import YoutubeVideoLightbox from "../components/YoutubeVideoLightbox";
import {
  deleteApplicantJobPostNotForMe,
  deleteApplicantJobPostSaved,
  getApplicantMyApplicationForJobPost,
  getApplicantPublishedJobPost,
  getHiringJobPost,
  getPublicPublishedJobPost,
  isApiConfigured,
  postApplicantApplicationPitchVideo,
  postApplicantJobPostInteractionStatus,
  putApplicantJobPostNotForMe,
  putApplicantJobPostSaved,
} from "../apiClient";
import { JOB_POST_PREVIEW_ROUTE_SLUG, createEmptyPreviewJobPost } from "../data/jobPostPreview";
import { buildAuthHrefWithNext } from "../navigateAfterAuth";
import { isApplicantProfileSetupComplete } from "../lib/applicantProfileNotification";
import { jobPublicHref } from "../lib/jobPublicPath";
import { jobSearchReturnRouterState, readJobSearchRestoreSnapshot } from "../jobSearch/jobSearchRestore";
import { buildJobSearchParamsFromState } from "../jobSearch/jobSearchUrlParams";
import { GUEST_JOB_WHY_VIDEO_ID } from "../lib/youtubeVideos";

const APPLY_HELP_SAMPLE_VIDEO =
  "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4";

function GuideIcon({ name, className = "icon", ...rest }) {
  if (name === "row-view") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M3 6.5a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1zm0 8a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "square-view") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M3 9.4c0-2.24 0-3.36.436-4.216a4 4 0 0 1 1.748-1.748C6.04 3 7.16 3 9.4 3h5.2c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C21 6.04 21 7.16 21 9.4v5.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C17.96 21 16.84 21 14.6 21H9.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C3 17.96 3 16.84 3 14.6z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "not-for-me") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="m18 9-.84 8.398c-.127 1.273-.19 1.909-.48 2.39a2.5 2.5 0 0 1-1.075.973C15.098 21 14.46 21 13.18 21h-2.36c-1.279 0-1.918 0-2.425-.24a2.5 2.5 0 0 1-1.076-.973c-.288-.48-.352-1.116-.48-2.389L6 9m7.5 6.5v-5m-3 5v-5m-6-4h4.615m0 0 .386-2.672c.112-.486.516-.828.98-.828h3.038c.464 0 .867.342.98.828l.386 2.672m-5.77 0h5.77m0 0H19.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "applied") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M9.713 3.64c.581-.495.872-.743 1.176-.888a2.58 2.58 0 0 1 2.222 0c.304.145.595.393 1.176.888.599.51 1.207.768 2.007.831.761.061 1.142.092 1.46.204.734.26 1.312.837 1.571 1.572.112.317.143.698.204 1.46.063.8.32 1.407.83 2.006.496.581.744.872.889 1.176.336.703.336 1.52 0 2.222-.145.304-.393.595-.888 1.176a3.3 3.3 0 0 0-.831 2.007c-.061.761-.092 1.142-.204 1.46a2.58 2.58 0 0 1-1.572 1.571c-.317.112-.698.143-1.46.204-.8.063-1.407.32-2.006.83-.581.496-.872.744-1.176.889a2.58 2.58 0 0 1-2.222 0c-.304-.145-.595-.393-1.176-.888a3.3 3.3 0 0 0-2.007-.831c-.761-.061-1.142-.092-1.46-.204a2.58 2.58 0 0 1-1.571-1.572c-.112-.317-.143-.698-.204-1.46a3.3 3.3 0 0 0-.83-2.006c-.496-.581-.744-.872-.89-1.176a2.58 2.58 0 0 1 .001-2.222c.145-.304.393-.595.888-1.176.52-.611.769-1.223.831-2.007.061-.761.092-1.142.204-1.46a2.58 2.58 0 0 1 1.572-1.571c.317-.112.698-.143 1.46-.204a3.3 3.3 0 0 0 2.006-.83"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="m8.667 12.633 1.505 1.721a1 1 0 0 0 1.564-.073L15.333 9.3"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "saved") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M7.527 20.841C6.861 21.274 6 20.772 6 19.952V3.942c0-.52.336-.942.75-.942h10.5c.414 0 .75.422.75.942v16.01c0 .82-.861 1.322-1.527.89l-3.946-2.562a.96.96 0 0 0-1.054 0z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "chevron-left") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path d="m15 6-6 6 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "chevron-right") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "close") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "play") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M13.242 10.71c.672.547 1.008.821 1.008 1.29s-.336.743-1.008 1.29c-.185.152-.37.295-.538.413a9 9 0 0 1-.49.318c-.67.407-1.006.611-1.306.385s-.328-.697-.383-1.642A14 14 0 0 1 10.5 12c0-.235.01-.497.025-.764.055-.945.082-1.417.383-1.642.3-.226.636-.022 1.306.385.174.106.341.214.49.318.169.118.353.261.538.412"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "minimise") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M20 10h-6V4M4 14h6v6M20 4l-6 6m-4 4-6 6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "maximise") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M14 4h6v6M10 20H4v-6M20 4l-6 6M4 20l6-6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "save") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M16.25 21v-4.765a1.59 1.59 0 0 0-1.594-1.588H9.344a1.59 1.59 0 0 0-1.594 1.588V21m8.5-17.715v2.362a1.59 1.59 0 0 1-1.594 1.588H9.344A1.59 1.59 0 0 1 7.75 5.647V3m8.5.285A3.2 3.2 0 0 0 14.93 3H7.75m8.5.285c.344.156.661.374.934.645l2.382 2.375A3.17 3.17 0 0 1 20.5 8.55v9.272A3.18 3.18 0 0 1 17.313 21H6.688A3.18 3.18 0 0 1 3.5 17.823V6.176A3.18 3.18 0 0 1 6.688 3H7.75"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "applicants") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <title>Applicants</title>
        <path
          d="M21 19.75c0-2.09-1.67-5.068-4-5.727m-2 5.727c0-2.651-2.686-6-6-6s-6 3.349-6 6m9-12.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0m3 3a3 3 0 1 0 0-6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "rejected-applicants") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <title>Rejected applicants</title>
        <path
          d="M15 7.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0m4.5 13c-.475-9.333-14.525-9.333-15 0m6.086-3 2.828 2.828m0-2.828-2.828 2.828"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "accepted-applicant") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <title>Accepted Applicants</title>
        <path
          d="M15 7.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0m4.5 13c-.475-9.333-14.525-9.333-15 0"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="m10.258 18.992 1.034 1.181c.095.109.266.1.35-.016l2.1-2.907"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "new-user") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <title>New applicants</title>
        <path
          d="M15.019 17h-6.04m6.04 0h3.614c1.876 0 1.559-1.86.61-2.804C15.825 10.801 20.68 3 11.999 3s-3.825 7.8-7.243 11.196c-.913.908-1.302 2.804.61 2.804H8.98m6.039 0c0 1.925-.648 4-3.02 4s-3.02-2.075-3.02-4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M15 13.5c0-3.861-6-3.861-6 0"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 10.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "check") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="m6 13.626 1.606 1.722c.886.95 1.329 1.424 1.825 1.574.436.131.9.096 1.315-.1.473-.224.852-.761 1.612-1.836L18 7"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "info") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 16v-5h-.5m0 5h1M12 8.5V8"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "locations") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M12 21a9 9 0 1 0 0-18m0 18a9 9 0 1 1 0-18m0 18c2.761 0 3.941-5.163 3.941-9S14.761 3 12 3m0 18c-2.761 0-3.941-5.163-3.941-9S9.239 3 12 3M3.5 9h17m-17 6h17"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "email") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="m2.357 7.714 6.98 4.654c.963.641 1.444.962 1.964 1.087.46.11.939.11 1.398 0 .52-.125 1.001-.446 1.964-1.087l6.98-4.654M7.157 19.5h9.686c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.31-1.311c.328-.642.328-1.482.328-3.162V9.3c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311c-.642-.327-1.482-.327-3.162-.327H7.157c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.31 1.311c-.328.642-.328 1.482-.328 3.162v5.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311c.642.327 1.482.327 3.162.327"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "mobile") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M16.286 3H7.714C6.768 3 6 3.806 6 4.8v14.4c0 .994.768 1.8 1.714 1.8h8.572c.947 0 1.714-.806 1.714-1.8V4.8c0-.994-.767-1.8-1.714-1.8M10.5 6h3"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "facebook") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M6.5 10v4h3v7h4v-7h3l1-4h-4V8c0-.545.455-1 1-1h3V3h-3c-2.723 0-5 2.277-5 5v2z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "linkedin") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <title>LinkedIn</title>
        <path
          d="M3 9.4c0-2.24 0-3.36.436-4.216a4 4 0 0 1 1.748-1.748C6.04 3 7.16 3 9.4 3h5.2c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C21 6.04 21 7.16 21 9.4v5.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C17.96 21 16.84 21 14.6 21H9.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C3 17.96 3 16.84 3 14.6zm5-1.775v.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8 16.375V10.75m4 5.625V13.5m0 0v-2.75m0 2.75c0-1.288 1.222-2 2.4-2 1.6 0 1.6 1.375 1.6 2.875v2"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "instagram") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <title>Instagram</title>
        <path
          d="M3 9.4c0-2.24 0-3.36.436-4.216a4 4 0 0 1 1.748-1.748C6.04 3 7.16 3 9.4 3h5.2c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C21 6.04 21 7.16 21 9.4v5.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C17.96 21 16.84 21 14.6 21H9.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C3 17.96 3 16.84 3 14.6zm14-2.9h.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M15.462 11.487a3.5 3.5 0 1 1-6.925 1.026 3.5 3.5 0 0 1 6.925-1.026"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "link") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="m15.988 13 3.902-3.902c1.437-1.437 1.485-3.718.107-5.095-1.377-1.378-3.658-1.33-5.095.107L11 8.012m2 7.95-3.892 3.88c-1.432 1.43-3.64 1.615-5.082.107-1.442-1.507-1.326-3.639.107-5.068L8.025 11M9 15l6-6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "youtube") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <title>YouTube</title>
        <path
          d="M2 12c0-3.3 0-4.95 1.464-5.975C4.93 5 7.286 5 12 5s7.071 0 8.535 1.025S22 8.7 22 12s0 4.95-1.465 5.975C19.072 19 16.714 19 12 19s-7.071 0-8.536-1.025S2 15.3 2 12"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10.5 9.908v4.184a.41.41 0 0 0 .412.408.4.4 0 0 0 .228-.068l3.175-2.074a.405.405 0 0 0 .003-.678l-3.175-2.11a.415.415 0 0 0-.573.11.4.4 0 0 0-.07.228"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "refresh") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <title>Refresh</title>
        <path
          d="M20.5 8c-1.392-3.179-4.823-5-8.522-5C7.299 3 3.453 6.552 3 11.1"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M16.489 8.4h3.97A.54.54 0 0 0 21 7.86V3.9M3.5 16c1.392 3.179 4.823 5 8.522 5 4.679 0 8.525-3.552 8.978-8.1"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7.511 15.6h-3.97a.54.54 0 0 0-.541.54v3.96"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return null;
}

function formatAppliedOnFromIso(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function JobDetailsPage({ currentUser, setCurrentUser, authToken }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobId } = useParams();
  /** Prefer React state; fall back to localStorage so interactions match Job Search after refresh. */
  const effectiveAuthToken = String(
    authToken || (typeof window !== "undefined" ? window.localStorage.getItem("jobuwin-auth-token") : "") || ""
  ).trim();
  const [showApplyHelp, setShowApplyHelp] = useState(false);
  const [showProfileIncomplete, setShowProfileIncomplete] = useState(false);
  const [showApplyShineModal, setShowApplyShineModal] = useState(false);
  const [showGuestWhyVideo, setShowGuestWhyVideo] = useState(false);
  const applyHelpVideoRef = useRef(null);

  const [serverApplicationPitchUrl, setServerApplicationPitchUrl] = useState("");
  const [serverApplicationPitchLoaded, setServerApplicationPitchLoaded] = useState(false);

  const [jobPostInteraction, setJobPostInteraction] = useState({
    notForMe: false,
    saved: false,
    applied: false,
    appliedAt: null,
    applicationStatus: null,
    rejectionLetterHtml: null,
  });
  const jobPostInteractionRef = useRef(jobPostInteraction);
  useLayoutEffect(() => {
    jobPostInteractionRef.current = jobPostInteraction;
  }, [jobPostInteraction]);
  const [jobPostInteractionLoaded, setJobPostInteractionLoaded] = useState(false);
  const [jobPostInteractionError, setJobPostInteractionError] = useState("");

  const isApplicantProfileIncomplete = !isApplicantProfileSetupComplete(currentUser);

  const closeApplyHelp = useCallback(() => {
    setShowApplyHelp(false);
    const video = applyHelpVideoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }, []);

  useEffect(() => {
    if (!showApplyHelp) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeApplyHelp();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeApplyHelp, showApplyHelp]);

  useEffect(() => {
    if (!showProfileIncomplete) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setShowProfileIncomplete(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showProfileIncomplete]);

  const isUuidJobId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(jobId || "")
  );
  const isPreviewJobRoute = String(jobId || "") === JOB_POST_PREVIEW_ROUTE_SLUG;
  const [remoteHiringJob, setRemoteHiringJob] = useState(null);
  const [remoteHiringJobError, setRemoteHiringJobError] = useState("");
  const [remoteHiringJobLoading, setRemoteHiringJobLoading] = useState(false);

  const applicantJobUuid = useMemo(() => {
    const raw = String(jobId || "").trim();
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)) return raw;
    const id = remoteHiringJob?.id != null ? String(remoteHiringJob.id).trim() : "";
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id) ? id : "";
  }, [jobId, remoteHiringJob?.id]);

  useEffect(() => {
    if (isPreviewJobRoute) {
      setRemoteHiringJob(null);
      setRemoteHiringJobError("");
      setRemoteHiringJobLoading(false);
      return undefined;
    }
    const key = String(jobId || "").trim();
    if (!key) {
      setRemoteHiringJob(null);
      setRemoteHiringJobError("");
      setRemoteHiringJobLoading(false);
      return undefined;
    }

    const t = effectiveAuthToken;
    if (!t) {
      if (!isApiConfigured()) {
        setRemoteHiringJob(null);
        setRemoteHiringJobError("Job search is not available (API not configured).");
        setRemoteHiringJobLoading(false);
        return undefined;
      }
      let cancelled = false;
      (async () => {
        try {
          setRemoteHiringJobError("");
          setRemoteHiringJobLoading(true);
          const { jobPost } = await getPublicPublishedJobPost(key);
          if (cancelled) return;
          setRemoteHiringJob(jobPost || null);
          const slug = String(jobPost?.publicSlug || "").trim();
          if (slug && isUuidJobId && slug !== key) {
            navigate(`/jobs/${encodeURIComponent(slug)}`, { replace: true, state: location.state });
          }
        } catch (e) {
          if (!cancelled) {
            setRemoteHiringJob(null);
            setRemoteHiringJobError(e instanceof Error ? e.message : "Could not load job post.");
          }
        } finally {
          if (!cancelled) setRemoteHiringJobLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    const loadAsApplicant = currentUser?.role === "applicant";
    let cancelled = false;
    (async () => {
      try {
        setRemoteHiringJobError("");
        setRemoteHiringJobLoading(true);
        let fetchId = key;
        if (!isUuidJobId) {
          const { jobPost: pub } = await getPublicPublishedJobPost(key);
          fetchId = String(pub?.id || "").trim();
          if (!fetchId) {
            throw new Error("Could not resolve job.");
          }
        }
        const { jobPost } = loadAsApplicant
          ? await getApplicantPublishedJobPost(fetchId, t)
          : await getHiringJobPost(fetchId, t);
        if (cancelled) return;
        setRemoteHiringJob(jobPost || null);
      } catch (e) {
        if (cancelled) return;
        setRemoteHiringJobError(e instanceof Error ? e.message : "Could not load job post.");
        setRemoteHiringJob(null);
      } finally {
        if (!cancelled) setRemoteHiringJobLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    currentUser?.role,
    effectiveAuthToken,
    isPreviewJobRoute,
    isUuidJobId,
    jobId,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    if (isPreviewJobRoute || !applicantJobUuid || currentUser?.role !== "applicant") {
      setServerApplicationPitchUrl("");
      setServerApplicationPitchLoaded(false);
      return undefined;
    }
    const t = effectiveAuthToken;
    if (!t || !isApiConfigured()) {
      setServerApplicationPitchUrl("");
      setServerApplicationPitchLoaded(false);
      return undefined;
    }
    let cancelled = false;
    setServerApplicationPitchLoaded(false);
    (async () => {
      try {
        const { application } = await getApplicantMyApplicationForJobPost(applicantJobUuid, t);
        if (cancelled) return;
        setServerApplicationPitchUrl(
          application?.pitchVideoUrl ? String(application.pitchVideoUrl).trim() : ""
        );
      } catch {
        if (!cancelled) setServerApplicationPitchUrl("");
      } finally {
        if (!cancelled) setServerApplicationPitchLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveAuthToken, currentUser?.role, isPreviewJobRoute, applicantJobUuid, jobId]);

  useEffect(() => {
    if (!applicantJobUuid || isPreviewJobRoute || currentUser?.role !== "applicant") {
      setJobPostInteraction({
        notForMe: false,
        saved: false,
        applied: false,
        appliedAt: null,
        applicationStatus: null,
        rejectionLetterHtml: null,
      });
      setJobPostInteractionLoaded(true);
      setJobPostInteractionError("");
      return undefined;
    }
    const t = effectiveAuthToken;
    if (!t || !isApiConfigured()) {
      setJobPostInteraction({
        notForMe: false,
        saved: false,
        applied: false,
        appliedAt: null,
        applicationStatus: null,
        rejectionLetterHtml: null,
      });
      setJobPostInteractionLoaded(true);
      setJobPostInteractionError("");
      return undefined;
    }
    let cancelled = false;
    setJobPostInteractionLoaded(false);
    setJobPostInteractionError("");
    (async () => {
      try {
        const by = await postApplicantJobPostInteractionStatus([applicantJobUuid], t);
        const row = by[applicantJobUuid];
        if (!cancelled) {
          setJobPostInteraction({
            notForMe: Boolean(row?.notForMe),
            saved: Boolean(row?.saved),
            applied: Boolean(row?.applied),
            appliedAt: row?.appliedAt != null && String(row.appliedAt).trim() !== "" ? String(row.appliedAt) : null,
            applicationStatus: row?.applicationStatus != null ? String(row.applicationStatus) : null,
            rejectionLetterHtml:
              row?.rejectionLetterHtml != null && String(row.rejectionLetterHtml).trim() !== ""
                ? String(row.rejectionLetterHtml)
                : null,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setJobPostInteraction({
            notForMe: false,
            saved: false,
            applied: false,
            appliedAt: null,
            applicationStatus: null,
            rejectionLetterHtml: null,
          });
          setJobPostInteractionError(
            e instanceof Error ? e.message : "Could not load your job preferences from the server."
          );
        }
      } finally {
        if (!cancelled) setJobPostInteractionLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveAuthToken, currentUser?.role, isPreviewJobRoute, applicantJobUuid]);

  const previewJobPostFromState =
    location.state && typeof location.state.previewJobPost === "object" ? location.state.previewJobPost : null;
  const previewJobPost = isPreviewJobRoute
    ? previewJobPostFromState || createEmptyPreviewJobPost()
    : null;
  const job = previewJobPost || remoteHiringJob;
  const hasExplicitBack = Boolean(location.state?.fromPath);
  const backPath = location.state?.fromPath || (currentUser ? "/applicant-home" : "/");
  const backLabel = location.state?.fromLabel || "Back";
  const backReturnState = useMemo(() => {
    if (backPath === "/job-search") {
      const routerUi = location.state?.returnState?.searchUiState;
      const snapshot = readJobSearchRestoreSnapshot();
      const ui = routerUi || snapshot;
      if (ui) return jobSearchReturnRouterState(ui);
    }
    if (location.state?.returnState?.searchUiState) {
      return location.state.returnState;
    }
    return location.state?.returnState;
  }, [backPath, location.state]);
  const backTo = useMemo(() => {
    if (backPath !== "/job-search") return backPath;
    const ui = location.state?.returnState?.searchUiState || readJobSearchRestoreSnapshot();
    if (!ui) return backPath;
    try {
      const qs = buildJobSearchParamsFromState(ui).toString();
      return qs ? `/job-search?${qs}` : backPath;
    } catch {
      return backPath;
    }
  }, [backPath, location.state]);
  const isApplicantUser = currentUser?.role === "applicant";
  const appliedOnFromNavigation =
    typeof location.state?.appliedOn === "string" ? String(location.state.appliedOn).trim() : "";
  const userHasAppliedToRole =
    Boolean(appliedOnFromNavigation) || (jobPostInteractionLoaded && jobPostInteraction.applied);
  const appliedOnDisplayLabel =
    appliedOnFromNavigation ||
    (jobPostInteraction.appliedAt ? formatAppliedOnFromIso(jobPostInteraction.appliedAt) : "");
  const isApplicationRejected =
    userHasAppliedToRole && jobPostInteraction.applicationStatus === "rejected";
  const rejectionLetterHtml = jobPostInteraction.rejectionLetterHtml;

  const isJobExpired = (() => {
    const raw = job?.deadline;
    const iso = typeof raw === "string" ? raw.trim() : "";
    if (!iso) return false;
    // Deadline is date-only (YYYY-MM-DD). Treat it as expiring at end-of-day UTC.
    const endsAtUtcMs = Date.parse(`${iso}T23:59:59.999Z`);
    if (!Number.isFinite(endsAtUtcMs)) return false;
    return Date.now() > endsAtUtcMs;
  })();

  // Apply is only available to applicant accounts, and only for roles that are open & published.
  // (We currently only have draft/published on the backend, but keep the status checks future-proof.)
  const jobStatus = String(job?.status || "").trim().toLowerCase();
  const isJobApplyableStatus = jobStatus === "published" || jobStatus === "active";
  const isJobBlockedStatus = jobStatus === "draft" || jobStatus === "hidden" || jobStatus === "closed";
  const canApplyForRole = Boolean(
    job &&
      isApplicantUser &&
      !isJobExpired &&
      isJobApplyableStatus &&
      !isJobBlockedStatus &&
      !userHasAppliedToRole
  );
  const applyInteractionPending = Boolean(
    isApplicantUser &&
      applicantJobUuid &&
      !isPreviewJobRoute &&
      effectiveAuthToken &&
      isApiConfigured() &&
      !jobPostInteractionLoaded
  );

  const canSetJobInteraction = Boolean(
    job &&
      isApplicantUser &&
      applicantJobUuid &&
      !isPreviewJobRoute &&
      isApiConfigured() &&
      effectiveAuthToken &&
      !remoteHiringJobLoading &&
      !remoteHiringJobError
  );

  const refetchJobPostInteraction = useCallback(async () => {
    if (!applicantJobUuid) return;
    const t = effectiveAuthToken;
    if (!t) return;
    try {
      const by = await postApplicantJobPostInteractionStatus([applicantJobUuid], t);
      const row = by[applicantJobUuid];
      setJobPostInteraction({
        notForMe: Boolean(row?.notForMe),
        saved: Boolean(row?.saved),
        applied: Boolean(row?.applied),
        appliedAt: row?.appliedAt != null && String(row.appliedAt).trim() !== "" ? String(row.appliedAt) : null,
        applicationStatus: row?.applicationStatus != null ? String(row.applicationStatus) : null,
        rejectionLetterHtml:
          row?.rejectionLetterHtml != null && String(row.rejectionLetterHtml).trim() !== ""
            ? String(row.rejectionLetterHtml)
            : null,
      });
      setJobPostInteractionError("");
    } catch {
      setJobPostInteraction({
        notForMe: false,
        saved: false,
        applied: false,
        appliedAt: null,
        applicationStatus: null,
        rejectionLetterHtml: null,
      });
    }
  }, [effectiveAuthToken, applicantJobUuid]);

  const toggleDetailNotForMe = useCallback(async () => {
    if (!canSetJobInteraction) return;
    const t = effectiveAuthToken;
    const id = applicantJobUuid;
    setJobPostInteractionError("");
    const snap = jobPostInteractionRef.current;
    const willTurnOn = !snap.notForMe;
    setJobPostInteraction({
      notForMe: willTurnOn,
      saved: willTurnOn ? false : snap.saved,
      applied: snap.applied,
      appliedAt: snap.appliedAt,
    });
    try {
      if (willTurnOn) await putApplicantJobPostNotForMe(id, t);
      else await deleteApplicantJobPostNotForMe(id, t);
    } catch (e) {
      setJobPostInteractionError(e instanceof Error ? e.message : "Could not update “not for me”.");
      await refetchJobPostInteraction();
    }
  }, [canSetJobInteraction, effectiveAuthToken, applicantJobUuid, refetchJobPostInteraction]);

  const toggleDetailSaveLater = useCallback(async () => {
    if (!canSetJobInteraction) return;
    const t = effectiveAuthToken;
    const id = applicantJobUuid;
    setJobPostInteractionError("");
    const snap = jobPostInteractionRef.current;
    const willTurnOn = !snap.saved;
    setJobPostInteraction({
      saved: willTurnOn,
      notForMe: willTurnOn ? false : snap.notForMe,
      applied: snap.applied,
      appliedAt: snap.appliedAt,
    });
    try {
      if (willTurnOn) await putApplicantJobPostSaved(id, t);
      else await deleteApplicantJobPostSaved(id, t);
    } catch (e) {
      setJobPostInteractionError(e instanceof Error ? e.message : "Could not update “save for later”.");
      await refetchJobPostInteraction();
    }
  }, [canSetJobInteraction, effectiveAuthToken, applicantJobUuid, refetchJobPostInteraction]);

  const closeApplyShineModal = useCallback(() => {
    setShowApplyShineModal(false);
  }, []);

  const handleApplyPitchModalSubmit = useCallback(
    async ({ blob, mime }) => {
      const t = effectiveAuthToken;
      if (!isApiConfigured() || !t) {
        throw new Error(
          !isApiConfigured()
            ? "Submitting your application requires the API to be configured."
            : "Sign in to submit your application pitch."
        );
      }
      if (!applicantJobUuid) {
        throw new Error("Job is still loading. Try again in a moment.");
      }
      const { pitchVideoUrl } = await postApplicantApplicationPitchVideo(applicantJobUuid, blob, mime, t);
      const appliedOnStr = new Date().toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
      setServerApplicationPitchUrl(pitchVideoUrl ? String(pitchVideoUrl).trim() : "");
      setServerApplicationPitchLoaded(true);
      captureAnalyticsEvent("application_submitted", {
        job_post_id: applicantJobUuid,
      });
      setJobPostInteraction({
        notForMe: false,
        saved: false,
        applied: true,
        appliedAt: new Date().toISOString(),
      });
      navigate(jobPublicHref(remoteHiringJob || { id: applicantJobUuid }), {
        replace: true,
        state: {
          ...(location.state || {}),
          appliedOn: appliedOnStr,
        },
      });
    },
    [applicantJobUuid, effectiveAuthToken, location.state, navigate, remoteHiringJob]
  );

  const returnAfterAuthPath = useMemo(() => `${location.pathname}${location.search}`, [location.pathname, location.search]);
  const guestSignInHref = useMemo(() => buildAuthHrefWithNext("/signin", returnAfterAuthPath), [returnAfterAuthPath]);
  const guestSignUpHref = useMemo(() => buildAuthHrefWithNext("/signup", returnAfterAuthPath), [returnAfterAuthPath]);

  const handleHeaderApplyClick = useCallback(() => {
    captureAnalyticsEvent("application_started", {
      job_post_id: applicantJobUuid,
    });
    setShowApplyShineModal(true);
  }, [applicantJobUuid]);

  const handleHeaderProfileIncomplete = useCallback(() => {
    setShowProfileIncomplete(true);
  }, []);

  const jobSeoTitle = useMemo(() => (job ? buildJobPageSeoTitle(job) : "Job opening"), [job]);
  const jobSeoDescription = useMemo(
    () => (job ? buildJobPageSeoDescription(job) : "View job openings on JOBUWIN."),
    [job]
  );
  const jobSeoJsonLd = useMemo(() => (job && !isPreviewJobRoute ? buildJobPageJsonLd(job) : null), [job, isPreviewJobRoute]);
  const jobSeoKeywords = useMemo(() => (job ? buildJobPageKeywords(job) : ""), [job]);
  const jobSeoImagePath = useMemo(() => {
    const logo = String(job?.companyLogoUrl || "").trim();
    return logo || DEFAULT_OG_IMAGE_PATH;
  }, [job?.companyLogoUrl]);
  const jobSeoImageAlt = useMemo(() => {
    if (!job) return undefined;
    const company = String(job.company || "Company").trim() || "Company";
    return `${company} logo`;
  }, [job]);
  const isPublicJobSeoIndexable = useMemo(
    () => Boolean(job && !isPreviewJobRoute && isJobPubliclyIndexable(job)),
    [job, isPreviewJobRoute]
  );

  return (
    <Nd1JobDetailsShell
      currentUser={currentUser}
      setCurrentUser={setCurrentUser}
      authToken={authToken}
    >
      {isPreviewJobRoute ? (
        <PageSeo title="Job preview" description="Preview of a job listing." noindex />
      ) : remoteHiringJobLoading && !job ? (
        <PageSeo title="Loading job" description="Loading job details." noindex />
      ) : remoteHiringJobError ? (
        <PageSeo title="Job not available" description={remoteHiringJobError} noindex />
      ) : job ? (
        <PageSeo
          title={jobSeoTitle}
          description={jobSeoDescription}
          canonicalPath={jobPublicHref(job)}
          schemaType="website"
          jsonLd={jobSeoJsonLd}
          keywords={jobSeoKeywords}
          imagePath={jobSeoImagePath}
          imageAlt={jobSeoImageAlt}
          noindex={!isPublicJobSeoIndexable}
        />
      ) : (
        <PageSeo title="Job not found" description="This job listing is no longer available." noindex />
      )}
      <main id="nd1-main" className="nd1-job-detail-main applicant-home job-detail-page">
        <section className="nd1-shell nd1-section nd1-job-detail">
          <PageTopbar
            backTo={backTo}
            backLabel={backLabel}
            backState={backReturnState}
            onBackClick={(event) => {
              if (hasExplicitBack) return;
              event.preventDefault();
              navigate(-1);
            }}
            breadcrumbItems={
              job && !remoteHiringJobLoading && !remoteHiringJobError
                ? buildJobPageBreadcrumbItems(job)
                : null
            }
          />
          {remoteHiringJobLoading ? (
            <JobuwinPageLoading
              title="Loading job post…"
              subtitle="Fetching the latest version from the database."
            />
          ) : remoteHiringJobError ? (
            <div className="nd1-job-detail__state">
              <h1>Job not available</h1>
              <p className="subtitle">{remoteHiringJobError}</p>
            </div>
          ) : job ? (
            <>
              <JobDetailHeader
                job={job}
                currentUser={currentUser}
                guestSignInHref={guestSignInHref}
                canApplyForRole={canApplyForRole}
                canSetJobInteraction={canSetJobInteraction}
                jobPostInteraction={jobPostInteraction}
                jobPostInteractionLoaded={jobPostInteractionLoaded}
                userHasAppliedToRole={userHasAppliedToRole}
                applyInteractionPending={applyInteractionPending}
                isApplicantProfileIncomplete={isApplicantProfileIncomplete}
                onApplyClick={handleHeaderApplyClick}
                onSaveClick={() => void toggleDetailSaveLater()}
                onProfileIncomplete={handleHeaderProfileIncomplete}
              />
              <JobListingContent job={job} layout="detail" showMetaCards={false} />

              {job && !isPreviewJobRoute && !currentUser && isApiConfigured() ? (
                <JobPostCommentsSection
                  key={`guest-comments-${String(jobId)}`}
                  jobPostId=""
                  currentUser={null}
                  authToken=""
                  layout="detail"
                  publicSlugOrId={String(jobId)}
                />
              ) : null}

              {currentUser && !isPreviewJobRoute && applicantJobUuid ? (
                <JobPostCommentsSection
                  key={applicantJobUuid}
                  jobPostId={applicantJobUuid}
                  currentUser={currentUser}
                  authToken={authToken}
                  layout="detail"
                />
              ) : null}

              {isApplicantUser && userHasAppliedToRole ? (
                <div className="nd1-job-detail-actions job-actions-float job-actions-float--applied">
                  <div className="job-applied-summary">
                    <div className="job-applied-summary-badge">
                      <GuideIcon name="applied" className="job-applied-summary-icon" aria-hidden="true" />
                      <p className="job-applied-status">
                        {appliedOnDisplayLabel
                          ? `Applied on ${appliedOnDisplayLabel}`
                          : "You have applied to this role"}
                      </p>
                    </div>
                    {isApplicationRejected ? (
                      <div className="job-applied-rejection">
                        <h3 className="job-applied-rejection__title">Application update</h3>
                        {rejectionLetterHtml ? (
                          <ApplicantRejectionLetter
                            letterHtml={rejectionLetterHtml}
                            className="job-applied-rejection__letter"
                          />
                        ) : (
                          <p className="job-applied-rejection__summary">
                            Your application was not successful this time. Check your notifications for details.
                          </p>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : currentUser ? (
                <div
                  className={`nd1-job-detail-actions job-actions-float${
                    canApplyForRole ? "" : " job-actions-float--blocked"
                  }`}
                >
                  {!canApplyForRole ? (
                    <p className="job-apply-blocked-hint">
                      <strong>This role isn&apos;t open for applications right now.</strong>
                    </p>
                  ) : null}
                  {jobPostInteraction.notForMe || jobPostInteraction.saved ? (
                    <p
                      className={`job-actions-status ${
                        jobPostInteraction.notForMe
                          ? "job-actions-status--not-for-me"
                          : "job-actions-status--saved"
                      }`}
                    >
                      {jobPostInteraction.notForMe
                        ? "You have marked this job as unsuitable."
                        : "You have saved this job."}
                    </p>
                  ) : null}
                  <div className="nd1-job-detail-actions__toolbar job-actions-float-row">
                    <div className="nd1-job-detail-actions__secondary job-actions-buttons">
                      <button
                        type="button"
                        className={`job-action-btn job-action-btn--not-for-me${
                          jobPostInteraction.notForMe ? " job-action-btn--not-for-me-active" : ""
                        }`}
                        aria-pressed={jobPostInteraction.notForMe}
                        disabled={!canSetJobInteraction || !jobPostInteractionLoaded}
                        onClick={() => void toggleDetailNotForMe()}
                      >
                        <GuideIcon name={jobPostInteraction.notForMe ? "check" : "not-for-me"} />
                        Not for me
                      </button>
                      <button
                        type="button"
                        className={`job-action-btn job-action-btn--save-later${
                          jobPostInteraction.saved ? " job-action-btn--save-later-active" : ""
                        }`}
                        aria-pressed={jobPostInteraction.saved}
                        disabled={!canSetJobInteraction || !jobPostInteractionLoaded}
                        onClick={() => void toggleDetailSaveLater()}
                      >
                        <Heart
                          className="icon job-action-btn__heart"
                          size={18}
                          strokeWidth={1.75}
                          fill="currentColor"
                          aria-hidden="true"
                        />
                        Save for later
                      </button>
                      {jobPostInteractionError ? (
                        <p className="apply-shine-error" role="alert">
                          {jobPostInteractionError}
                        </p>
                      ) : null}
                    </div>
                    <div className="nd1-job-detail-actions__primary">
                      <button
                        type="button"
                        className="job-action-btn primary"
                        disabled={!canApplyForRole || applyInteractionPending}
                        onClick={() => {
                          if (!canApplyForRole || applyInteractionPending) return;
                          if (isApplicantProfileIncomplete) {
                            setShowProfileIncomplete(true);
                            return;
                          }
                          captureAnalyticsEvent("application_started", {
                            job_post_id: applicantJobUuid,
                          });
                          setShowApplyShineModal(true);
                        }}
                      >
                        <GuideIcon name="applied" />
                        Apply
                      </button>
                      <button
                        type="button"
                        className="job-apply-help-trigger"
                        disabled={!canApplyForRole}
                        onClick={() => setShowApplyHelp(true)}
                        aria-haspopup="dialog"
                        aria-expanded={showApplyHelp}
                      >
                        <GuideIcon name="info" className="job-apply-help-icon" aria-hidden="true" />
                        <span className="job-apply-help-text">How &quot;Apply&quot; works?</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="nd1-job-detail-actions job-actions-float jw-guest-job-actions">
                  <div className="jw-guest-job-actions__row">
                    <div className="jw-guest-job-actions__auth">
                      <Link
                        to={guestSignInHref}
                        className="nd1-btn nd1-btn--outline jw-guest-job-actions__btn"
                      >
                        Sign in
                      </Link>
                      <Link
                        to={guestSignUpHref}
                        className="nd1-btn nd1-btn--primary jw-guest-job-actions__btn"
                      >
                        Sign up
                      </Link>
                    </div>
                    <button
                      type="button"
                      className="jw-guest-watch-cta jw-guest-watch-cta--dock jw-guest-job-actions__watch-btn"
                      onClick={() => setShowGuestWhyVideo(true)}
                      aria-haspopup="dialog"
                      aria-expanded={showGuestWhyVideo}
                      aria-label="Watch this: Why join us? Opens a short video."
                    >
                      <img
                        src={`${process.env.PUBLIC_URL || ""}/images/guest-watch-siren.gif`}
                        alt=""
                        className="jw-guest-watch-cta__gif"
                        width={44}
                        height={44}
                      />
                      <span className="jw-guest-watch-cta__label">Watch this: Why join us?</span>
                    </button>
                  </div>
                </div>
              )}

              {isPreviewJobRoute ? (
                <div className="job-preview-close-footer">
                  <button
                    type="button"
                    className="large-filled-button job-preview-close-button"
                    onClick={() => {
                      if (hasExplicitBack) {
                        navigate(backTo, { state: backReturnState });
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    Close
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <div className="nd1-job-detail__state">
              <h1>Job not found</h1>
              <p className="subtitle">This listing isn&apos;t available anymore.</p>
            </div>
          )}
        </section>
      </main>

      <PitchRecordModal
        open={showApplyShineModal}
        onRequestClose={closeApplyShineModal}
        titleId="apply-shine-title"
        title="It is your time to shine..."
        introParagraphs={[
          "Tell the hiring manager why you believe you are suitable for this job. Be honest, be truthful and use examples from your experience.",
        ]}
        limitLine="Your pitch video must be 5 minutes or less."
        submitLabel="Submit application"
        onSubmitPitch={handleApplyPitchModalSubmit}
        analyticsContext="application"
        pitchVideoAriaLabel="Application pitch preview"
        showMobilePitchLink
        applyHelpVideoSrc={APPLY_HELP_SAMPLE_VIDEO}
      />

      {showProfileIncomplete ? (
        <div
          className="apply-help-overlay"
          role="presentation"
          onClick={() => setShowProfileIncomplete(false)}
        >
          <div
            className="apply-help-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Complete your profile before applying"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="apply-help-close"
              onClick={() => setShowProfileIncomplete(false)}
              aria-label="Close"
            >
              <GuideIcon name="close" />
            </button>
            <p className="profile-incomplete-message" id="profile-incomplete-message">
              Before you can apply, upload a profile photo and your CV on your profile page.
            </p>
            <div className="profile-incomplete-actions">
              <button
                type="button"
                className="large-filled-button"
                onClick={() => {
                  setShowProfileIncomplete(false);
                  navigate("/applicant-profile");
                }}
              >
                Update my profile
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showApplyHelp ? (
        <div className="apply-help-overlay" role="presentation" onClick={closeApplyHelp}>
          <div
            className="apply-help-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="apply-help-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button type="button" className="apply-help-close" onClick={closeApplyHelp} aria-label="Close">
              <GuideIcon name="close" />
            </button>
            <h2 id="apply-help-title" className="apply-help-title">
              How &quot;Apply&quot; works.
            </h2>
            <video
              ref={applyHelpVideoRef}
              className="apply-help-video"
              controls
              playsInline
              preload="metadata"
            >
              <source src={APPLY_HELP_SAMPLE_VIDEO} type="video/mp4" />
              Your browser does not support embedded video.
            </video>
            <div className="apply-help-body">
              <p>
                Because we only want genuine people like you to apply for jobs on our platform and ensure no fake
                people are able to apply, we do things differently.
              </p>
              <p>
                When you press &quot;Apply&quot; you have the opportunity to explain to the hiring manager or the
                recruiter exactly why you feel you are suitable for the job. This needs to be a short video from your
                computer or mobile. Once you do this, we will share your profile along with the pitch. This makes sure
                only genuine people will apply.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <YoutubeVideoLightbox
        open={showGuestWhyVideo}
        onClose={() => setShowGuestWhyVideo(false)}
        videoId={GUEST_JOB_WHY_VIDEO_ID}
        title="Why join us?"
      />
    </Nd1JobDetailsShell>
  );
}

export default JobDetailsPage;
